// Paathshala — Ancient Jain Manuscript Theme
// Inspired by Kalpa Sutra manuscripts: parchment, vermillion ink, gold leaf

export const COLORS = {
  // Backgrounds
  parchment: '#E4D1AF',
  parchmentDark: '#D6C49E',
  parchmentDarker: '#C8B690',

  // Text — vermillion red ink (primary readable text)
  redInk: '#8B1A1A',
  redInkLight: '#A52A2A',

  // Gold leaf — ONLY for backgrounds, dividers, button fills. NEVER for readable text.
  goldLeaf: '#C5A55A',
  goldLeafDark: '#A8893E',

  // Secondary text, badges, micro-text
  darkBrown: '#3E2723',
  brown: '#5D4037',

  // Deep contrast (rare)
  nearBlack: '#1A0F0A',
} as const;

export const FONTS = {
  heading: "'Playfair Display', Georgia, 'Times New Roman', serif",
  body: "'Crimson Text', Georgia, 'Times New Roman', serif",
} as const;

// Google Fonts URLs to load dynamically
export const FONT_URLS = [
  'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&display=swap',
  'https://fonts.googleapis.com/css2?family=Crimson+Text:ital,wght@0,400;0,600;0,700;1,400;1,600&display=swap',
];

// ── Parchment texture layers (fixed, full viewport) ─────────────
// Layered SVG-noise data URIs: grain + fibers + stains + vignette,
// rendered as fixed divs (backgroundAttachment: 'fixed' is broken on
// iOS Safari).

const GRAIN_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0.24 0 0 0 0 0.15 0 0 0 0 0.06 0 0 0 0.16 0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E\")";

const FIBERS_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='600'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='turbulence' baseFrequency='0.012 0.05' numOctaves='2' seed='7' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0.36 0 0 0 0 0.24 0 0 0 0 0.1 0 0 0 0.05 0'/%3E%3C/filter%3E%3Crect width='600' height='600' filter='url(%23f)'/%3E%3C/svg%3E\")";

const FIXED_LAYER: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',
};

export const PARCHMENT_GRAIN_STYLE: React.CSSProperties = {
  ...FIXED_LAYER,
  opacity: 0.5,
  backgroundImage: GRAIN_SVG,
  backgroundSize: '300px 300px',
};

export const PARCHMENT_FIBERS_STYLE: React.CSSProperties = {
  ...FIXED_LAYER,
  opacity: 0.45,
  backgroundImage: FIBERS_SVG,
  backgroundSize: '600px 600px',
};

export const PARCHMENT_STAINS_STYLE: React.CSSProperties = {
  ...FIXED_LAYER,
  background: [
    'radial-gradient(ellipse 40% 26% at 12% 8%, rgba(168,137,62,0.10), transparent 70%)',
    'radial-gradient(ellipse 32% 22% at 88% 30%, rgba(139,26,26,0.05), transparent 70%)',
    'radial-gradient(ellipse 45% 30% at 20% 78%, rgba(168,137,62,0.09), transparent 70%)',
    'radial-gradient(ellipse 30% 24% at 85% 92%, rgba(93,64,55,0.08), transparent 70%)',
  ].join(', '),
};

export const PARCHMENT_VIGNETTE_STYLE: React.CSSProperties = {
  ...FIXED_LAYER,
  background:
    'radial-gradient(ellipse 120% 110% at 50% 45%, transparent 55%, rgba(62,39,35,0.16) 88%, rgba(26,15,10,0.26) 100%)',
};

// Card background — semi-transparent overlay on the parchment texture
export const CARD_BG_STYLE: React.CSSProperties = {
  backgroundColor: 'rgba(62, 39, 35, 0.06)',
  backdropFilter: 'blur(1px)',
  border: `1px solid rgba(62, 39, 35, 0.1)`,
};

// Vignette overlay for worn edges (still used by SutraShowcase)
export const VIGNETTE_STYLE: React.CSSProperties = {
  boxShadow: 'inset 0 0 100px rgba(62, 39, 35, 0.15), inset 0 0 200px rgba(62, 39, 35, 0.05)',
};

// Manuscript-style CTA buttons (uppercase Crimson Text, gold leaf fill)
export const BTN_BASE_STYLE: React.CSSProperties = {
  fontFamily: FONTS.body,
  fontSize: '17px',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  borderRadius: '3px',
  minHeight: '54px',
};

export const BTN_GOLD_STYLE: React.CSSProperties = {
  ...BTN_BASE_STYLE,
  color: COLORS.nearBlack,
  background: `linear-gradient(170deg, ${COLORS.goldLeaf}, ${COLORS.goldLeafDark})`,
  border: '1px solid rgba(26,15,10,0.25)',
  boxShadow:
    '0 2px 0 rgba(26,15,10,0.22), 0 10px 26px -8px rgba(139,26,26,0.35), inset 0 1px 0 rgba(255,244,214,0.45)',
};

export const BTN_OUTLINE_STYLE: React.CSSProperties = {
  ...BTN_BASE_STYLE,
  color: COLORS.redInk,
  background: 'transparent',
  border: '1.5px solid rgba(139,26,26,0.55)',
};
